import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import TopMenu from "components/CustomerList/TopMenu";
import List from "components/common/ListWithMoreOptions";
import Modal from "components/common/ModalFullScreen";
import AlertIcon from "components/icons/AlertIcon";
import useCustomers from "graphql/useCustomers";
import useCustomerMutations from "graphql/useCustomerMutations";
import _ from "lodash";
import Loading from "components/common/loading";
import { useTranslation } from "react-i18next";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "../../components/common/BottomPageSelectorWithPagination";
import { usePaginatedData } from "../../helpers/hooks";
import { useMessaging } from "../../context/MessagingContext";
import { buildCustomerName } from "../../helpers/utils";
import CustomerForm from "./FormCustomer";
import s from "./styles";

const SCAPE_VALUE = "\\";

function fixValueForQuery(value) {
  return value
    .replace(`${SCAPE_VALUE}`, `${SCAPE_VALUE}${SCAPE_VALUE}`)
    .replace("*", `${SCAPE_VALUE}*`);
}

function CustomerList() {
  const { t } = useTranslation();

  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);

  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination("name");

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const [profileViewList, setProfileViewList] = useState("list");

  const { deleteCustomer } = useCustomerMutations();
  const { customers: customer = [], loading } = useCustomers();

  const data = useMemo(() => {
    let customersListFilteredProfile = customer;
    if (profileViewList !== "all") {
      customersListFilteredProfile = customer.filter(
        (i) =>
          i.tags &&
          i.tags.length > 0 &&
          i.tags[0].toLocaleLowerCase().includes(profileViewList)
      );
    }
    return customersListFilteredProfile.filter((i) => {
      const regex = new RegExp(fixValueForQuery(search), "i");
      return (
        (i.name && i.name.match(regex)) ||
        (i.lastName && i.lastName.match(regex)) ||
        (i.name && i.lastName && `${i.name} ${i.lastName}`.match(regex)) ||
        (i.address1 && i.address1.match(regex)) ||
        (i.idNumber && i.address1.match(regex)) ||
        i.ref?.toLocaleLowerCase().includes(search)
      );
    });
  }, [customer, search, profileViewList]);

  const dataToShow = usePaginatedData(data, pagination, sorting, (item) => [
    buildCustomerName(item),
    item.idNumber,
    item.ref,
    item.email,
    `${item.address1 ? `${item.address1}, ` : ""}${
      item.address2 ? `${item.address2}, ` : ""
    }${item.city ? `${item.city} ` : ""}`,
    moment(item.updateDatetime).format("lll"),
  ]);

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.CustomerList.customerEditMessage"),
      info1: t("containers.CustomerList.customerReadyMessage"),
      info2: t("containers.CustomerList.customerFindMessage"),
    });
  }, []);
  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.CustomerList.customerAddedMessage"),
      info1: t("containers.CustomerList.customerReadyMessage"),
      info2: t("containers.CustomerList.customerFindMessage"),
    });
  }, []);

  const onDeleteRequest = useCallback((customer) => {
    setConfirmMessage({
      titleAlert: t("containers.CustomerList.delete"),
      confirm1: `${t(
        "containers.CustomerList.deleteCustomer"
      )} ${buildCustomerName(customer)}?`,
      confirm2: t("containers.CustomerList.deleteCustomerUndone"),
      onConfirm: () =>
        deleteCustomer(customer)
          .then(() => {
            setAddCustomerModalOpen(false);
            setCustomerToEdit(null);
          })
          .catch((e) => setErrorMessage({ error: e })),
    });
  }, []);

  return (
    <s.PageContainer>
      <TopMenu
        changeSearchValue={(v) => changeSearch(v)}
        setrolViewList={setProfileViewList}
        addWorker={() => setAddCustomerModalOpen(true)}
        loading={loading}
      />

      {loading ? (
        <Loading />
      ) : (
        <>
          <s.ListContainer>
            {/* show no content yet */}
            {customer.length === 0 && (
              <s.NoCustomerContainer>
                <s.NoCustomersMessageCenter>
                  <AlertIcon
                    styles={{ display: "block", marginBottom: "7px" }}
                  />
                  {t("containers.CustomerList.noCustomer")}
                  <s.NoCustomersMessageSecond>
                    {t("containers.CustomerList.noCustomerSuggestion")}
                  </s.NoCustomersMessageSecond>
                </s.NoCustomersMessageCenter>
              </s.NoCustomerContainer>
            )}

            {customer?.length > 0 && (
              <List
                width="1300px"
                handleEdit={(customer) => {
                  setCustomerToEdit(_.cloneDeep(customer.element));
                  setAddCustomerModalOpen(true);
                }}
                handleDeleteWorker={(customer) =>
                  onDeleteRequest(customer.element)
                }
                data={dataToShow}
                columns={[
                  {
                    title: t("containers.CustomerList.name"),
                    sortable: true,
                    columnId: "name",
                  },
                  {
                    title: t("containers.CustomerList.nif"),
                    sortable: true,
                    columnId: "idNumber",
                  },
                  {
                    title: t("containers.CustomerList.ref"),
                    sortable: true,
                    columnId: "ref",
                  },
                  {
                    title: t("containers.CustomerList.email"),
                    sortable: true,
                    columnId: "email",
                  },
                  {
                    title: t("containers.CustomerList.address"),
                    sortable: true,
                    columnId: "address1",
                  },
                  {
                    title: t("containers.CustomerList.updateDatetime"),
                    sortable: true,
                    columnId: "updateDatetime",
                  },
                  {
                    title: "",
                  },
                ]}
                sorting={sorting}
                changeSorting={(columnId, order) =>
                  setSorting({ columnId, order })
                }
                tableStyle={{
                  gridTemplateColumns: "20fr 10fr 10fr 13fr 13fr 20fr 5fr",
                }}
              />
            )}
          </s.ListContainer>
          <BottomPageSelectorWithPagination
            pagination={pagination}
            setPagination={setPagination}
            length={customer.length}
          />
        </>
      )}

      {addCustomerModalOpen && (
        <Modal>
          <CustomerForm
            closeModal={() => {
              setAddCustomerModalOpen(false);
              setCustomerToEdit(null);
            }}
            customer={customerToEdit}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
            onDeleteRequest={onDeleteRequest}
          />
        </Modal>
      )}
    </s.PageContainer>
  );
}

export default CustomerList;
